import React from 'react';
import { TitleBar } from 'components/Layouts/TitleBar';
import { FormattedMessage, useIntl } from 'react-intl';
import { Container, Stack, Typography } from '@mui/material';

export const PrivacyPolicyPage = () => {
  const { formatMessage } = useIntl();

  return (
    <Stack flexGrow={1}>
      <TitleBar title={formatMessage({ id: 'PrivacyPolicyPage.Title', defaultMessage: 'Privacy Policy' })} />
      <Stack flexGrow={1} paddingX={6} sx={{
        backgroundColor: 'grey.100'
      }}>
        <Container maxWidth='xl' sx={{ bgcolor: 'background.paper', pt: 4, flexGrow: 1 }}>
          <Stack gap={4}>
            <Stack gap={1}>
              <Typography>{
                <FormattedMessage
                  id='PrivacyPolicyPage.Body'
                  defaultMessage={'This privacy policy applies to the EzDesk website (<website>https://portal.ezdesk.ca</website>). The protection of the personal information you provide to us is important to us. That is why we have implemented strict safeguards and security standards. In addition, we will not sell or rent your personal information to third parties for marketing purposes. Please read this privacy policy to learn more about how we use and protect your personal information. If you have any questions or concerns about this policy, please contact us by email at <email>info@ariv.ca</email> or by phone at <phone>1-450-286-0848.</phone>'}
                  values={{
                    website: chunks => <a href='https://portal.ezdesk.ca' target='_blank' rel="noreferrer">{chunks}</a>,
                    email: chunks => <a href='mailto:info@ariv.ca'>{chunks}</a>,
                    phone: chunks => <a href='tel:14502860848'>{chunks}</a>
                  }}
                />
              }</Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h4'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.Overview.Title',
                  defaultMessage: 'overview'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.Overview.Body'
                  defaultMessage={`
                      The privacy practices in this policy apply to our services for the portal.ezdesk.ca domain and subdomains. By using this website, you agree to be bound by the terms and conditions of this privacy policy. If you do not agree to these terms, please do not use or access our site. This Privacy Policy describes the information we collect and how we use it in the normal operation of our services. We adhere to the privacy practices described in this policy and, subject to the provisions of applicable law, strive to implement a consistent set of privacy practices for the portal.ezdesk.ca user community. By accepting the Privacy Policy and User Agreement upon registration, you expressly consent to our use and disclosure of your personal information in accordance with this Privacy Policy.
                      <p>This policy is effective upon acceptance of registration for new users, and as of January 25, 2023 for all other users.</p>
                    `}
                  values={{
                    p: chunks => <p>{chunks}</p>
                  }}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.InformationWeCollect.Title',
                  defaultMessage: '1. The information we collect'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.InformationWeCollect.Body'
                  defaultMessage={`
                      Our primary goal in collecting your personal information is to provide you with a safe, efficient and personalized experience. This information allows us to maintain the effectiveness of our site, to provide you with services and features that meet your needs, and to ensure that it remains a useful and relevant source of information. We collect personal information only for these purposes.
                      <p>To open a service request on portal.ezdesk.ca, however, you must provide contact information such as your name and email address.</p>
                      <p>When you provide us with your personal information, you are no longer anonymous to us. If you choose to use our site, we may ask you for your contact information, identity, and other personal information, depending on the forms on portal.ezdesk.ca. Where applicable, we will indicate which fields are required and which are optional. You always have the option of not providing information by choosing not to use a particular service or feature. We do not retain your personal information from one visit to the next. We only use cookies to store information for a single session - and during that session. Web browsers use a small amount of space on your hard drive to store these preferences, and each time you visit a Web site, your browser checks to see if you have set preferences (cookies) for that server. If you have, your browser sends the cookie to the server with the request to connect to a Web page. Your browser will not send its cookie data to any server other than the one that set it. If you communicate with us (e-mail, letter, etc.), we may compile this information in a file pertaining to you.</p>
                    `}
                  values={{
                    p: chunks => <p>{chunks}</p>
                  }}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.OurUseOfYourInformation.Title',
                  defaultMessage: '2. Our use of your information'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.OurUseOfYourInformation.Body'
                  defaultMessage={'We use your personal information to provide you with better service. We use the information in your file as well as information collected from your browsing of portal.ezdesk.ca to: resolve disputes; diagnose problems; detect and prevent error, fraud and other criminal activity; enforce our user agreement; and as otherwise described to you at the time of collection, use or disclosure of your personal information. We may cross-reference and review your personal information for errors, omissions or inaccuracies. You agree that we may also use your personal information to contact you to provide you with information, administrative notices, and in some cases, offer you products and services relevant to your use of portal.ezdesk.ca. By accepting the User Agreement and Privacy Policy, you expressly agree to receive this information.'}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.ExternalServiceProvider.Title',
                  defaultMessage: '3. External service providers'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.ExternalServiceProvider.Body'
                  defaultMessage={'There are a number of separate services - web hosting, web analytics and marketing services - offered by third parties (external service providers) that may be complementary to your use of portal.ezdesk.ca. If you choose to use these separate services, disclose information to these external service providers and/or allow them to collect information about you, their use of your information is governed by their own privacy policies. We do not disclose your personal information to external service providers unless you give us your express consent. With your consent and for all purposes, we may share some of your personal information with an outside service provider that offers such services. If you do not want us to disclose your personal information to an external service provider, you may withhold your consent or simply not use their services. Since we do not control the privacy practices of these third parties, you should evaluate their practices before deciding to use their services.'}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.InternalServiceProvidersForOurOperations.Title',
                  defaultMessage: '4. Internal service providers for our operations'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.InternalServiceProvidersForOurOperations.Body'
                  defaultMessage={'We may use third parties (internal service providers) to facilitate or outsource the management of one or more aspects of the business or operation of the products and services we offer you. We may therefore disclose some of your personal information to these internal service providers. These service providers are subject to confidentiality agreements with us and other legal restrictions that prevent them from using the personal information we provide to them for any purpose other than to facilitate the particular transaction related to ezdesk.ca.'}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.OtherLegalEntities.Title',
                  defaultMessage: '5. Other legal entities'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.OtherLegalEntities.Body'
                  defaultMessage={'We may share our data, including your personal information, with our subsidiaries, joint ventures, potential partners or assignors. To the extent that these entities may have access to your information, they will treat it with at least the same care as information collected from their own users. Our subsidiaries, joint ventures, prospective partners or assignors will adhere to privacy practices no less stringent than those described in this document, to the extent permitted by applicable law.'}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.LegalRequests.Title',
                  defaultMessage: '6. Legal requests'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.LegalRequests.Body'
                  defaultMessage={'ARIV inc (portal.ezdesk.ca) cooperates with the competent authorities in matters of legal investigations as well as with any third party responsible for the application of laws such as legislation relating to the protection of intellectual property, fraud and other rights in order to protect the members of the ezdesk.ca community. Therefore, in response to a request by law enforcement or other governmental agency (a warrant, subpoena, legal process) in connection with a criminal investigation or an allegation of illegal activity, we may (because you have authorized us to do so), without a warrant, disclose your name, city, province, phone number, email address, and usage history related to your user ID. However, in order to balance the protection of your privacy with the protection of society from malicious persons, we will not disclose any additional information to the police or any other law enforcement authority without a subpoena, warrant, court order, or similar legal process, except in cases where the disclosure of additional information is necessary to prevent imminent physical danger or financial loss.'}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.OtherInformationCollectors.Title',
                  defaultMessage: '7. Other information collectors'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.OtherInformationCollectors.Body'
                  defaultMessage={'Except as otherwise stated in this Privacy Policy, this document only addresses the use and disclosure of information we collect about you. If you provide your personal information to third parties, whether users of portal.ezdesk.ca or other sites on the Internet, different rules may apply to their use or disclosure. To the extent that portal.ezdesk.ca has no control over the privacy policies of third parties, you are subject to the practices and policies of those third parties. We encourage you to ask questions before disclosing your personal information to third parties.'}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.Security.Title',
                  defaultMessage: '8. Security'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.Security.Body'
                  defaultMessage={'Your information is stored and processed on our servers. Portal.ezdesk.ca uses technical and procedural safeguards, such as firewalls and encryption, to protect your personal information from loss or theft, as well as from unauthorized access or disclosure. We use a variety of security techniques to protect this data from unauthorized access by users inside and outside the company. However, "perfect security" does not exist on the Internet.'}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.Notice.Title',
                  defaultMessage: '9. Notice'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.Notice.Body'
                  defaultMessage={'If we make any material changes to our privacy practices that do not affect personal information already collected on our site, we will post a prominent notice on our website informing users of the change. All amended terms and conditions will automatically become effective 30 days after they are initially posted on portal.ezdesk.ca.'}
                />
              </Typography>
            </Stack>

            <Stack gap={1}>
              <Typography textTransform='uppercase' variant='h5'>{
                formatMessage({
                  id: 'PrivacyPolicyPage.LinksToOtherSites.Title',
                  defaultMessage: 'Links to other sites'
                })
              }</Typography>
              <Typography variant='body1'>
                <FormattedMessage
                  id='PrivacyPolicyPage.LinksToOtherSites.Body'
                  defaultMessage={`
                    This site contains links to other websites over which ARIV Inc. has no control (portal.ezdesk.ca). Please be aware that we are not responsible for the privacy practices of such sites.</p>
                    <p>By using the ARIV Inc. website and services (portal.ezdesk.com), you signify your acceptance of this Privacy Policy. If you do not agree or are not comfortable with any statement in this Privacy Policy, you should discontinue your use of the ARIV Inc. website (portal.ezdesk.ca) immediately. ARIV Inc (portal.ezdesk.ca) reserves the right to change this privacy policy at any time.
                    <p>We encourage you to be aware when you leave our site and to read the privacy statement of each and every website that collects personally identifiable information.</p>
                    <p>
                      This privacy statement applies solely to information collected by this website.<br></br>
                      ARIV Inc (portal.ezdesk.ca)<br></br>
                      321 Saint-Charles Street West<br></br>
                      Longueuil, Quebec J4H 1E5<br></br>
                      Email: <email>info@ariv.ca</email>
                    </p>
                    `}
                  values={{
                    p: chunks => <p>{chunks}</p>,
                    br: () => <br/>,
                    email: chunks => <a href='mailto:info@ariv.ca'>{chunks}</a>
                  }}
                />
              </Typography>
            </Stack>

          </Stack>
        </Container>
      </Stack>
    </Stack>
  );
};